"use client";

import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Monitor,
  Smartphone,
  Megaphone,
  Bot,
  Briefcase,
  Network,
  ArrowRight,
  Sparkles,
  CheckCircle,
} from "lucide-react";

export default function CoreSolution() {
  const navItems = [
    {
      name: "Web Development",
      content:
        "Create responsive, high-performance websites that look great and deliver results. Our expert developers use cutting-edge technologies to build scalable web solutions.",
      icon: <Monitor />,
      color: "from-blue-500 to-cyan-500",
      features: [
        "Responsive Design",
        "SEO Optimized",
        "Fast Loading",
        "Secure & Scalable",
      ],
      image: "/coreimg.png",
    },
    {
      name: "App Development",
      content:
        "Build mobile apps for iOS and Android with great user experience. Native and cross-platform solutions tailored to your business needs.",
      icon: <Smartphone />,
      color: "from-purple-500 to-pink-500",
      features: [
        "Cross-Platform",
        "Native Performance",
        "App Store Ready",
        "Push Notifications",
      ],
      image: "/app.jpg",
    },
    {
      name: "Digital Marketing",
      content:
        "Boost your online presence through effective marketing strategies. Data-driven campaigns that deliver measurable results.",
      icon: <Megaphone />,
      color: "from-orange-500 to-red-500",
      features: [
        "SEO & SEM",
        "Social Media",
        "Content Marketing",
        "Analytics & Reporting",
      ],
      image: "/marketing.jpg",
    },
    {
      name: "Automation",
      content:
        "Automate your business workflows and save time. Streamline processes and increase efficiency with intelligent automation solutions.",
      icon: <Bot />,
      color: "from-green-500 to-emerald-500",
      features: [
        "Workflow Automation",
        "Process Optimization",
        "Integration APIs",
        "Custom Solutions",
      ],
      image: "/automation.jpg",
    },
    {
      name: "AI Solution",
      content:
        "Integrate AI to improve decision-making and customer experience. Machine learning and AI-powered solutions for modern businesses.",
      icon: <Briefcase />,
      color: "from-indigo-500 to-purple-500",
      features: [
        "Machine Learning",
        "Natural Language Processing",
        "Predictive Analytics",
        "Computer Vision",
      ],
      image: "/ai.jpg",
    },
    {
      name: "ERP Solution",
      content:
        "Manage your enterprise resources efficiently with our ERP solutions. Comprehensive business management systems for growing companies.",
      icon: <Network />,
      color: "from-teal-500 to-blue-500",
      features: [
        "Resource Planning",
        "Inventory Management",
        "Financial Tracking",
        "Reporting Dashboard",
      ],
      image: "/erp.jpg",
    },
  ];

  const [activeIndex, setActiveIndex] = useState(0);

  return (
    <div className="bg-gradient-to-br from-gray-50 via-white to-blue-50 py-10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm border border-gray-200 rounded-full px-3 py-1.5 mb-4">
            <Sparkles className="w-4 h-4 text-[#fd3e3d]" />
            <span className="text-sm font-medium text-gray-700">
              Our Core Solutions
            </span>
          </div>
          <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-3">
            Comprehensive Tech Solutions
          </h2>
          <p className="text-base text-gray-600 max-w-2xl mx-auto">
            From web development to AI integration, we provide end-to-end tech
            solutions to move your business forward.
          </p>
        </motion.div>

        {/* Tabs */}
        <div className="mb-10">
          <div className="flex flex-wrap justify-center gap-2 p-2 bg-white/60 backdrop-blur-sm rounded-2xl border border-gray-200 shadow-sm">
            {navItems.map((item, index) => (
              <motion.button
                key={index}
                onClick={() => setActiveIndex(index)}
                className={`relative px-4 py-2 rounded-lg text-sm transition-all duration-300 font-medium ${
                  index === activeIndex
                    ? "text-white shadow"
                    : "text-gray-600 hover:text-gray-900 hover:bg-white/50"
                }`}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {index === activeIndex && (
                  <motion.div
                    layoutId="activeTab"
                    className={`absolute inset-0 bg-gradient-to-r ${item.color} rounded-lg`}
                    transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                  />
                )}
                <span className="relative z-10 flex items-center gap-2">
                  {React.cloneElement(item.icon, { className: "w-5 h-5" })}
                  {item.name}
                </span>
              </motion.button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeIndex}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.4 }}
            className="grid lg:grid-cols-2 gap-10 items-center"
          >
            {/* Left Content */}
            <div className="space-y-6">
              <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-200">
                {/* Title */}
                <div className="flex items-center gap-3 mb-5">
                  <div
                    className={`p-2 rounded-lg bg-gradient-to-r ${navItems[activeIndex].color} text-white`}
                  >
                    {React.cloneElement(navItems[activeIndex].icon, {
                      className: "w-5 h-5",
                    })}
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {navItems[activeIndex].name}
                    </h3>
                    <div className="w-10 h-0.5 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full mt-1" />
                  </div>
                </div>

                <p className="text-sm text-gray-600 leading-relaxed mb-6">
                  {navItems[activeIndex].content}
                </p>

                {/* Features */}
                <div className="space-y-2 mb-6">
                  <h4 className="font-semibold text-sm text-gray-900 mb-2">
                    Key Features:
                  </h4>
                  {navItems[activeIndex].features.map((feature, idx) => (
                    <motion.div
                      key={idx}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: idx * 0.1 }}
                      className="flex items-center gap-2 text-sm text-gray-700"
                    >
                      <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                      {feature}
                    </motion.div>
                  ))}
                </div>

                {/* <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className={`inline-flex items-center gap-2 px-4 py-2 text-sm bg-gradient-to-r ${navItems[activeIndex].color} text-white font-medium rounded-lg hover:shadow-md transition-all duration-300`}
                >
                  Learn More
                  <ArrowRight className="w-4 h-4" />
                </motion.button> */}
              </div>
            </div>

            {/* Right Image */}
            <div className="relative">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
                className="relative"
              >
                <div
                  className={`absolute -inset-3 bg-gradient-to-r ${navItems[activeIndex].color} rounded-2xl opacity-20 blur-2xl`}
                />
                <div className="relative bg-white rounded-2xl p-6 shadow-xl border border-gray-200">
                  <img
                    src={navItems[activeIndex].image}
                    alt={`${navItems[activeIndex].name} illustration`}
                    className="w-full h-auto rounded-xl"
                  />

                  <div className="absolute -top-3 -right-3 bg-white rounded-full p-2 shadow-md border border-gray-200">
                    <div
                      className={`p-1.5 rounded-full bg-gradient-to-r ${navItems[activeIndex].color}`}
                    >
                      {React.cloneElement(navItems[activeIndex].icon, {
                        className: "w-4 h-4 text-white",
                      })}
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Floating Decorations */}
              <div className="absolute top-1/2 -left-6 w-10 h-10 bg-blue-100 rounded-full opacity-50 animate-pulse" />
              <div className="absolute bottom-1/4 -right-4 w-8 h-8 bg-purple-100 rounded-full opacity-50 animate-pulse delay-1000" />
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}
